import * as React from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { Location, useLocation, useNavigate } from 'react-router-dom'

import api from '@disco/api/server'
import {
  authenticate,
  setDiscoPlaylist,
} from '@disco/state-management/actions/UploadActions'
import { pushUINotification } from '@disco/utils/notifications'
import { getUrlParts } from '@disco/utils/url'

import type { DiscoPlaylist } from 'types/inbox'
import type { AddToDiscoUser, LoginDetails, User } from 'types/user'

import LoginForm from 'components/common/login-form'
import { Box } from 'components/ds'
import DiscoModal from 'components/ui/disco-modal'

import type { RootState } from 'store/configureStore'

const _signupUrlAsync = () => Promise.resolve('https://disco.ac/signup')

const getBusiness = async () => {
  return await api.getCurrentBusinessPublic()
}

const UploadAuth: React.FC<Props> = ({
  authenticate,
  setDiscoPlaylist,
  discoPlaylist,
  location,
  userAuth,
}) => {
  const navigate = useNavigate()

  const _onClose = React.useCallback(() => {
    const searchParams = new URLSearchParams(location.search)
    const signature = searchParams.get('signature')

    // if user didn't login reset chosen playlist
    if (!userAuth.loggedIn) {
      setDiscoPlaylist(null)
    } else {
      pushUINotification({
        message:
          "Playlist added! Please click 'Send files' below to send your list.",
        level: 'success',
      })
    }

    const parts = getUrlParts(location.pathname)
    const path = parts.slice(0, -1).join('/')

    navigate(`/${path}/?signature=${signature}`, { replace: true })
  }, [
    location.search,
    location.pathname,
    userAuth.loggedIn,
    setDiscoPlaylist,
    navigate,
  ])

  React.useEffect(() => {
    if (userAuth.loggedIn) _onClose()
  }, [userAuth.loggedIn, _onClose])

  const _handleLogin = async (data: LoginDetails) => {
    authenticate(data, discoPlaylist.business.hostname)
  }

  const _handleForgotPassword = (email: string, returnUrl: string) => {
    return api.sendPasswordRecoveryInstructions(email, returnUrl)
  }

  return (
    <DiscoModal contentLabel="Authenticate" onRequestClose={_onClose}>
      <Box p="3">
        <LoginForm
          user={{ ...userAuth, preloaded: true }}
          location={location}
          message="Sign in to add this music to your DISCO"
          onLoginUser={loginDetails => _handleLogin(loginDetails)}
          onForgotPassword={_handleForgotPassword}
          onGetSignupUrl={_signupUrlAsync}
          onGetBusiness={getBusiness}
        />
      </Box>
    </DiscoModal>
  )
}

const mapStateToProps = (state: RootState) => ({
  userAuth: state.upload.userAuth,
  discoPlaylist: state.upload.discoPlaylist,
})

const mapDispatchToProps = {
  setDiscoPlaylist,
  authenticate,
}

const connector = connect(mapStateToProps, mapDispatchToProps)

type PropsFromRedux = ConnectedProps<typeof connector>

type OwnProps = {
  location: Location
}

type Props = PropsFromRedux & OwnProps

const ConnectedUploadAuth = connector(UploadAuth)

const UploadAuthWithLocation = (props: any) => {
  const location = useLocation()
  return <ConnectedUploadAuth {...props} location={location} />
}

export default UploadAuthWithLocation
